import React, { useState } from "react";
import PropTypes from 'prop-types';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import M2Logo from "../assets/images/m2-horizontal-logo.png";
import Hero from "../assets/images/landing-hero-2x.gif";
import isEmail from 'validator/es/lib/isEmail';
import isURL from 'validator/es/lib/isURL';
import config from '../config';
import ReactGA from "react-ga4";
import {logForSentry} from "../utils";

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      By clicking the button above, you agree to our <Link href="https://www.monetizemore.com/monetizemore-terms-conditions-optimization-monetization-agreement/" target="_blank">terms of use</Link> and <Link href="https://www.monetizemore.com/privacy-policy/" target="_blank">privacy policies</Link>.
      <br />We will not provide, disclose or share personal data to or with third parties
    </Typography>
  );
}

// this sets the clientIP
let clientIP;
(async () => {
  try {
    const res = await fetch(config.geolocation_api_url);
    if (res && !res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const data = await res.json();
    clientIP = data.ip;
  } catch (e) {
    logForSentry(
        "Error fetching client IP",
        {
          error: e
        }
      )
  }
})();


export default function Landing({ handleSubmit }){
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [isWebsiteError, setIsWebsiteError] = useState(false);
  const [isEmailError, setIsEmailError] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const onWebsiteChange = (e) => {
    const urlValue = e.target.value;
    setWebsite(urlValue);
  }

  const onEmailChange = (e) => {
    setEmail(e.target.value);
  }

  const validateWebsite = async () => {
    const isValid = isURL(website);
    setIsButtonDisabled(true);
    if(isValid)
      await checkWebsiteValidation(website);
    else
      setIsWebsiteError(true)
  };

  const checkWebsiteValidation = async (uri) => {
    let res = await fetch(`${config.web_app_url}grader/validate-url?website=${uri}`);
    res.text().then(function (text) {
      if (text === "success") {
        setIsButtonDisabled(false)
        setIsWebsiteError(false);
        const urlParams = new URLSearchParams(window.location.search);
        const testMode=urlParams.get('testMode');
        if(testMode){
          fetch(`${config.pgai_bot_url}&url=${uri}`);
        }
        else{
          fetch(`${config.pgai_bot_url}?url=${uri}`);
        }
      }
      else
        setIsWebsiteError(true);
    });
  }

  const onWebsiteBlur = async () => {
    await validateWebsite();
  };

  const validateEmail = () => {
    const isValid = isEmail(email);
    setIsEmailError(!isValid);
    return isValid;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    let clientId;

    const submissionPage = window.location.href;

    if (isWebsiteError || !validateEmail()) {
      ReactGA.event('click_custom', {
        category: 'Engagement',
        label: 'Web Grader - "Get My Score" button clicked but form submission prevented',
      });
      return;
    }

    ReactGA.event('click_custom', {
      category: 'Conversion',
      label: 'Web Grader - "Get My Score" button clicked and form submission successful',
    });

    handleSubmit(website, email, clientId, clientIP, submissionPage);
  }

  return <Grid container component="main" sx={{
    backgroundColor: '#fafafa00',
    my: 8,
  }}>
    <Grid item xs={1} md={1}></Grid>
    <Grid item xs={10} md={4} component={Paper} elevation={0}>
      <Box
        sx={{
          my: 8,
          mx: 'auto',
        }}
      >
        <Link href='https://monetizemore.com'>
          <img id="horizontal-logo" src={M2Logo} alt="MonetizeMore Logo" loading="lazy" />
        </Link>

        <Typography component="h1" variant="h4" my={4} sx={{
          fontWeight: "bold",
          fontSize: 40,
          color: "#4A4C4D",
        }}>
          #1 AdOps Website Grader
        </Typography>
        Fill the form below to get your ad inventory analysis report in seconds & discover how to improve
        it for free to make maximum ad revenue.
        <Box
          component="form"
          noValidate
          onSubmit={onSubmit}
          sx={{
            mt: 4,
          }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="website"
            name="website"
            label="Website"
            autoComplete="website"
            sx={{
              mt: 0,
            }}
            error={isWebsiteError}
            helperText={isWebsiteError ? "Please Enter a valid website URL." : ""}
            onChange={onWebsiteChange}
            onBlur={onWebsiteBlur}
            value={website}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="email"
            type="email"
            id="email"
            label="Email"
            autoComplete="email"
            sx={{
              mt: 0,
            }}
            error={isEmailError}
            helperText={isEmailError ? "Please Enter a valid email address." : ""}
            onChange={onEmailChange}
            onBlur={validateEmail}
            value={email}
          />
          <Box sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isButtonDisabled}
            sx={{
              my: 2,
              px: 3,
              py: 1,
              backgroundColor: '#FEC129',
              color: '#4A4C4D',
              fontWeight: 600,
              fontSize: 15,
              '&:hover': {
                backgroundColor: '#D9A91B',
              },
          }}
          >
            Get My Score
          </Button>
          </Box>
          <Copyright sx={{ mt: 5, fontSize: 12 }} />
        </Box>
      </Box>
    </Grid>
    <Grid item md={1}></Grid>
    <Grid
      item
      md={6}
      sx={{ display: { xs: 'none', md: 'block'}}}
    >
      <img id="landing-hero-image" src={Hero} alt='hero' />
    </Grid>
  </Grid>;
}

Landing.propTypes = propTypes;